// Appointment-dated report stages (spec 2026-10-21 §3.1 / §4, backend PR #7026).
//
// Consultation Attended and Failed to Attend are counted from the consultation
// APPOINTMENT (its final status, dated by its `datetime`), not from the
// treatment's StateLog transition. Booked and Treatment Started keep the
// transition basis (§10), so only these two stages come through here.
//
// Endpoint contract (docs/reports/appointment-stages.md in dentalcrm):
//   GET practices/{p}/reports/appointment-stages/?start=YYYY-MM-DD&end=YYYY-MM-DD
//       [&stages=A,B | &stage=A][&period=day|week|month][&practice__in=1,2]
//       [&treatment_ids=…][&category_ids=…][&practitioner_ids=…][&source_types=…][&source_referrals=…]
//   GET …/appointment-stages/rows/?stage=A&start&end[&limit&offset][same filters]
// Unknown or repeated query params are a 400, so every URL is built here and
// nowhere else. Filter lists take at most 100 values; longer lists are chunked
// and the chunk results combined (a treatment sits in exactly one chunk and the
// backend de-duplicates per treatment per day, so sums stay exact).
import dayjs from 'dayjs';

import { callApi } from './apiCall';

export const APPOINTMENT_STAGE = Object.freeze({
    ATTENDED: 'CONSULTATION_ATTENDED',
    FTA: 'FAILED_TO_ATTEND_CONSULTATION',
});
export const APPOINTMENT_STAGES = Object.freeze([APPOINTMENT_STAGE.ATTENDED, APPOINTMENT_STAGE.FTA]);

export const isAppointmentStage = (status) => APPOINTMENT_STAGES.includes(status);

export const FILTER_CHUNK = 100;
export const ROWS_PAGE = 500; // backend maximum
export const ROWS_CAP = 5000; // same ceiling as the transition-basis popup

/** 'YYYY-MM-DD' from 'YYYY-MM-DD HH:mm:ss', an ISO string, a Date or a dayjs. */
export const toCalendarDate = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
        const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
        if (m) return m[1];
    }
    const d = dayjs(value);
    return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

const idList = (ids) => {
    let list = [];
    if (Array.isArray(ids)) list = ids;
    else if (ids != null) list = String(ids).split(',');
    return list.map((v) => String(v).trim()).filter(Boolean);
};

/** Split a filter list into ≤100-value chunks; `[null]` means "no filter". */
export const chunkFilter = (ids) => {
    const list = idList(ids);
    if (!list.length) return [null];
    const chunks = [];
    for (let i = 0; i < list.length; i += FILTER_CHUNK) chunks.push(list.slice(i, i + FILTER_CHUNK));
    return chunks;
};

const practiceList = (practiceIds) => idList(practiceIds);

const commonParams = ({ start, end, practiceIds, treatmentIds }) => {
    const p = new URLSearchParams();
    p.set('start', toCalendarDate(start));
    p.set('end', toCalendarDate(end));
    const practices = practiceList(practiceIds);
    // The URL's practice_pk already scopes a single practice; practice__in is
    // only for several (same rule as practiceInSuffix in analyticsReports).
    if (practices.length > 1) p.set('practice__in', practices.join(','));
    const treatments = idList(treatmentIds);
    if (treatments.length) p.set('treatment_ids', treatments.join(','));
    return p;
};

const primaryPractice = (practiceIds) => practiceList(practiceIds)[0];

export function buildAppointmentStageCountsUrl(practiceIds, { start, end, treatmentIds, stages = APPOINTMENT_STAGES, period } = {}) {
    const practiceId = primaryPractice(practiceIds);
    const p = commonParams({ start, end, practiceIds, treatmentIds });
    const wanted = idList(stages);
    if (wanted.length === 1) p.set('stage', wanted[0]);
    else if (wanted.length > 1) p.set('stages', wanted.join(','));
    if (period) p.set('period', period);
    return `/api/v1/practices/${practiceId}/reports/appointment-stages/?${p.toString()}`;
}

export function buildAppointmentStageRowsUrl(practiceIds, { stage, start, end, treatmentIds, limit = ROWS_PAGE, offset = 0 } = {}) {
    const practiceId = primaryPractice(practiceIds);
    const p = commonParams({ start, end, practiceIds, treatmentIds });
    p.set('stage', stage);
    p.set('limit', String(limit));
    if (offset) p.set('offset', String(offset));
    return `/api/v1/practices/${practiceId}/reports/appointment-stages/rows/?${p.toString()}`;
}

const failed = (res) => !res || res.ok === false;
const countsBody = (res) => (res?.data?.counts ? res.data : res?.data?.data) || null;

/**
 * Count for one stage from a counts response. `null` when the request failed
 * (a failed count must never render as a confident 0 — Overview convention).
 */
export const readStageCount = (res, stage) => {
    if (failed(res)) return null;
    const body = countsBody(res);
    if (!body || typeof body.counts !== 'object') return null;
    return Number(body.counts[stage] || 0);
};

/**
 * Counts for the appointment stages over one window.
 * @returns {{ ok: boolean, counts: Object<string, number>|null, status?: number }}
 */
export async function fetchAppointmentStageCounts(practiceIds, { start, end, treatmentIds, stages = APPOINTMENT_STAGES } = {}) {
    const chunks = chunkFilter(treatmentIds);
    const responses = await Promise.all(chunks.map((chunk) => callApi(
        buildAppointmentStageCountsUrl(practiceIds, { start, end, treatmentIds: chunk, stages })
    )));
    const bad = responses.find(failed);
    if (bad) return { ok: false, counts: null, status: bad?.status, data: bad?.data };
    const counts = {};
    idList(stages).forEach((stage) => {
        counts[stage] = responses.reduce((sum, res) => sum + (readStageCount(res, stage) || 0), 0);
    });
    return { ok: true, counts };
}

/**
 * Every drill-down row for one stage over one window (paged, capped at ROWS_CAP).
 * Rows: {appointment_id, lead_id, lead_treatment_id, treatment_id, datetime,
 *        status, stage, undated, first_name, last_name, treatment_name}.
 * @returns {{ ok: boolean, rows: Array, total: number, status?: number }}
 */
export async function fetchAppointmentStageRows(practiceIds, { stage, start, end, treatmentIds, onProgress, cap = ROWS_CAP } = {}) {
    const chunks = chunkFilter(treatmentIds);
    let rows = [];
    let total = 0;
    for (let c = 0; c < chunks.length; c += 1) {
        const chunk = chunks[c];
        const url = (offset) => buildAppointmentStageRowsUrl(practiceIds, { stage, start, end, treatmentIds: chunk, limit: ROWS_PAGE, offset });
        // eslint-disable-next-line no-await-in-loop
        const first = await callApi(url(0));
        if (failed(first)) return { ok: false, rows: [], total: 0, status: first?.status, data: first?.data };
        const firstRows = first.data?.results || [];
        const chunkTotal = Number(first.data?.count ?? firstRows.length);
        total += chunkTotal;
        rows = rows.concat(firstRows);
        onProgress?.({ loaded: rows.length, total });
        const want = Math.min(chunkTotal, cap - (rows.length - firstRows.length));
        for (let offset = firstRows.length; offset < want && firstRows.length; offset += ROWS_PAGE) {
            // eslint-disable-next-line no-await-in-loop
            const page = await callApi(url(offset));
            if (failed(page)) return { ok: false, rows: [], total: 0, status: page?.status, data: page?.data };
            const pageRows = page.data?.results || [];
            if (!pageRows.length) break;
            rows = rows.concat(pageRows);
            onProgress?.({ loaded: rows.length, total });
        }
        if (rows.length >= cap) break;
    }
    return { ok: true, rows: rows.slice(0, cap), total };
}
